import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flower2, Sun, Leaf, Snowflake } from "lucide-react";

const iconFor = {
    spring: Flower2,
    summer: Sun,
    fall: Leaf,
    winter: Snowflake,
};

const bgImages = {
    spring: "https://images.unsplash.com/photo-1490750967868-88aa4486c946?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    summer: "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    fall: "https://images.unsplash.com/photo-1507783548227-544c3b8fc065?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    winter: "https://images.unsplash.com/photo-1483664852095-d6cc6870702d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
};

export default function FourSeasons({ site }) {
    const [active, setActive] = useState(null);

    return (
        <section
            id="seasons"
            data-testid="seasons-section"
            className="relative bg-ink-800 py-24 md:py-36 border-y border-white/10"
        >
            <div className="mx-auto max-w-[1400px] px-5 md:px-10">
                <div className="mb-14 md:mb-20">
                    <div className="font-mono-label text-lime-signal mb-4">02 — Program</div>
                    <h2 className="font-display uppercase leading-[0.9] text-5xl md:text-7xl lg:text-8xl">
                        <span className="text-chrome">Four seasons.</span>
                        <br />
                        <span className="text-white">One team.</span>
                    </h2>
                    <p className="mt-6 max-w-lg text-white/60 leading-relaxed">
                        A year-round program that adapts to Alabama&apos;s weather. Consistent care from first bloom to first frost.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    {site.seasons.map((s, i) => {
                        const Icon = iconFor[s.key] || Leaf;
                        const isActive = active === s.key;
                        return (
                            <motion.div
                                key={s.key}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-100px" }}
                                transition={{ duration: 0.8, delay: i * 0.1, ease: [0.19, 1, 0.22, 1] }}
                                onMouseEnter={() => setActive(s.key)}
                                onMouseLeave={() => setActive(null)}
                                data-testid={`season-${s.key}`}
                                className="relative group cursor-default overflow-hidden border border-white/10 min-h-[420px] md:min-h-[560px] flex flex-col justify-between p-6 md:p-8"
                                style={{ background: "#0F0F0F" }}
                            >
                                <AnimatePresence>
                                    {isActive && (
                                        <motion.div
                                            key="bg"
                                            initial={{ opacity: 0, scale: 1.1 }}
                                            animate={{ opacity: 0.35, scale: 1 }}
                                            exit={{ opacity: 0 }}
                                            transition={{ duration: 0.6 }}
                                            className="absolute inset-0 bg-cover bg-center"
                                            style={{ backgroundImage: `url(${bgImages[s.key]})` }}
                                        />
                                    )}
                                </AnimatePresence>
                                <div
                                    className="absolute inset-0 pointer-events-none transition-opacity duration-500"
                                    style={{
                                        opacity: isActive ? 0.18 : 0,
                                        background: `radial-gradient(circle at 30% 30%, ${s.color} 0%, transparent 60%)`,
                                    }}
                                />
                                <div className="relative z-10 flex items-start justify-between">
                                    <span className="font-mono-label" style={{ color: s.color }}>
                                        {String(i + 1).padStart(2, "0")} · {s.key.toUpperCase()}
                                    </span>
                                    <Icon className="w-6 h-6" style={{ color: s.color }} strokeWidth={1.5} />
                                </div>
                                <div className="relative z-10">
                                    <h3 className="font-display uppercase text-4xl md:text-5xl leading-none mb-3 text-white">
                                        {s.label}
                                    </h3>
                                    <p className="text-white/60 text-sm leading-relaxed max-w-[26ch]">
                                        {s.desc}
                                    </p>
                                    <motion.div
                                        className="mt-6 h-px origin-left"
                                        style={{ background: s.color }}
                                        initial={{ scaleX: 0.15 }}
                                        animate={{ scaleX: isActive ? 1 : 0.15 }}
                                        transition={{ duration: 0.6 }}
                                    />
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
